import { ReactNode, useContext } from 'react'

import { useExternalArticleApi } from '@/common/api/modules/externalArticle'
import { dateTimeStr } from '@/common/form/date'

import TextButton from '@/components/view/atoms/button/TextButton'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'
import Image from '@/components/view/utils/image/Image'
import ExternalLink from '@/components/view/utils/link/ExternalLink'

import ExternalArticleStatusBadge from '@/components/container/page/site/externalArticle/ExternalArticleStatusBadge'

import { ExternalArticleDetailModalContext } from './ExternalArticleDetailModalProvider'

interface InnerProps {
  siteId: number
  articlePk: number
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <FormRow align='baseline'>
    <FormLabel fixWidth={128}>{label}</FormLabel>
    {children}
  </FormRow>
)

const ExternalArticleDetailModalInner = ({ siteId, articlePk }: InnerProps) => {
  const { data: detail, isLoading } = useExternalArticleApi({
    site_id: siteId,
    article_pk: articlePk
  })

  return (
    <ModalContent type={ModalContentType.FLEX_GROW}>
      {isLoading || !detail ? (
        <PageListSearching />
      ) : (
        <AreaContent>
          <ItemAlign gapX={16} align='start' nowrap>
            <Image
              src={detail.image_url || undefined}
              width={160}
              height={112}
              fitCover
              radius={8}
              border
            />
            <ItemAlign direction='vertical' gapY={4} remnant>
              <Text size={14} weight='bold'>
                {detail.title || '（タイトル未取得）'}
              </Text>
              <ExternalArticleStatusBadge status={detail.status} label={detail.status_label} />
              <TextButton size={12} color={TextColor.LINK} link={detail.url} newTab>
                {detail.url}
              </TextButton>
            </ItemAlign>
          </ItemAlign>
          <DetailRow label='記事ID'>
            <Text size={12}>{detail.article_id}</Text>
          </DetailRow>
          <DetailRow label='記事キー'>
            <FormRow direction='vertical' gap={2}>
              <Text size={12}>{detail.article_key}</Text>
              <FormCaption size={12}>計測・AIタグ・ベクトルで使う識別子です</FormCaption>
            </FormRow>
          </DetailRow>
          <DetailRow label='取得元'>
            <Text size={12}>{detail.source_host || '—'}</Text>
          </DetailRow>
          {detail.metadata_desc && (
            <DetailRow label='メタ説明'>
              <Text size={12}>{detail.metadata_desc}</Text>
            </DetailRow>
          )}
          <DetailRow label='公開日時'>
            <Text size={12}>
              {detail.published_at ? dateTimeStr(detail.published_at, { omitSecond: true }) : '—'}
            </Text>
          </DetailRow>
          <DetailRow label='最終取得'>
            <Text size={12}>
              {detail.last_fetched_at
                ? dateTimeStr(detail.last_fetched_at, { omitSecond: true })
                : '—'}
            </Text>
          </DetailRow>
          <DetailRow label='閲覧数(30日)'>
            <Text size={12}>{detail.view_count.toLocaleString()}</Text>
          </DetailRow>
          {detail.fail_reason && (
            <DetailRow label='理由'>
              <Text size={12} color={TextColor.CRITICAL}>
                {detail.fail_reason}
              </Text>
            </DetailRow>
          )}

          <FormCaption size={12}>
            本文の冒頭（{(detail.body_length ?? 0).toLocaleString()} 文字）
          </FormCaption>
          <Area noShadow bottomMargin={0}>
            <AreaContent>
              <Text size={12} preWrap>
                {detail.body_preview || '（本文なし）'}
              </Text>
            </AreaContent>
          </Area>
        </AreaContent>
      )}
    </ModalContent>
  )
}

// 外部記事の抽出結果（タイトル・画像・本文の冒頭など）を確認するモーダル
const ExternalArticleDetailModal = () => {
  const { modal, showing, hide } = useContext(ExternalArticleDetailModalContext)

  return (
    <Modal show={showing} type={ModalType.LARGE}>
      <ModalHeader text='抽出結果' onClickClose={hide} />
      {modal.showing && (
        <ExternalArticleDetailModalInner siteId={modal.siteId} articlePk={modal.articlePk} />
      )}
    </Modal>
  )
}

export default ExternalArticleDetailModal
